import * as React from 'react';
import * as Yup from 'yup';
import { omit } from 'lodash';
import { Field, FieldProps, Formik, FormikProps, FormikValues } from 'formik';
import { MutationFn } from 'react-apollo';
import {
  StyledForm,
  Card,
  Button,
  FieldRow,
  StyledFormGroup,
  StyledFormSelect,
  HeroImage,
  NewHeroImage,
} from '../../components';
import { formatFormValues } from '../../utils/helpers';
import { Span } from '../../typography';

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Inactive', value: 'ARCHIVED' },
  { label: 'Occupied', value: 'OCCUPIED' },
];

const TYPE_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Vacant Land', value: 'VACANT_LAND' },
  { label: 'Completed Property', value: 'COMPLETED_PROPERTY' },
  { label: 'City Centre', value: 'CITY_CENTRE' },
  { label: 'Apartments', value: 'APARTMENTS' },
  { label: 'Completed House', value: 'COMPLETED_HOUSE' },
  { label: 'High Density Sectional Title Unit', value: 'HIGH_DENSITY_SECTIONAL_TITLE_UNIT' },
  { label: 'Sectional Title Off Plan', value: 'SECTIONAL_TITLE_OFF_PLAN' },
  { label: 'Unreleased', value: 'UNRELEASED' },
  { label: 'Sectional Title Completed', value: 'SECTIONAL_TITLE_COMPLETED' },
  { label: 'Land Only', value: 'LAND_ONLY' },
  { label: 'Unset', value: 'UNSET' },
];

type DetailsFormValues = {
  size?: number;
  code?: string;
  rooms?: number;
  address?: string;
  suburb?: string;
  erfNumber?: string;
  salesAgent?: string;
  status?: { value: string; label: string };
  type?: { value: string; label: string };
};

const CreatePropertyValidation = Yup.object().shape({
  size: Yup.number().typeError('Must be a number').positive('Must be positive number').required('Required'),
  suburb: Yup.string().required('Required'),
  erfNumber: Yup.string().required('Required'),
  rooms: Yup.number().typeError('Must be a number').required('Required'),
  code: Yup.string().required('Required'),
  status: Yup.mixed().required('Required'),
  address: Yup.string().required('Required'),
  salesAgent: Yup.string().required('Required'),
  type: Yup.mixed(),
});

type Props = {
  error?: any;
  loading: boolean;
  buttonText?: string;
  mutation: MutationFn;
  isNewProperty?: boolean;
  initialValues: FormikValues;
  image?: { scaled: string; original: string; thumbnail: string };
};

const CreatePropertyForm: React.FC<Props> = (props) => {
  const [image, setImage] = React.useState(null);
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={CreatePropertyValidation}
      initialValues={
        props.initialValues
          ? props.initialValues
          : {
              code: '',
              address: '',
              size: '',
              suburb: '',
              rooms: '',
              erfNumber: '',
              status: '',
              salesAgent: 'Steyn City Real Estate',
              type: '',
            }
      }
      onSubmit={(values: DetailsFormValues) => {
        const formattedValues = formatFormValues(values);
        // TODO: Create form initialization objects for all forms to avoid passing unwanted values!
        const cleanValues = omit(formattedValues, ['stakeholders', 'image']);
        props.mutation({
          variables: image ? { ...cleanValues, image: { create: image } } : cleanValues,
        });
      }}
      render={(formikProps: FormikProps<DetailsFormValues>) => {
        return (
          <StyledForm flexDirection="row" flexWrap="nowrap">
            <Card flex={3} bg="white" p={4}>
              <FieldRow>
                <Field
                  name="code"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup {...field} mr={4} field={field} form={formikProps} label="Property ID" />
                  )}
                />
                <Field
                  name="erfNumber"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="ERF Number" />
                  )}
                />
              </FieldRow>
              <Field
                name="address"
                render={({ field }: FieldProps<DetailsFormValues>) => (
                  <StyledFormGroup {...field} field={field} form={formikProps} label="Address" />
                )}
              />
              <FieldRow>
                <Field
                  name="size"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup
                      {...field}
                      mr={4}
                      type="number"
                      min={0}
                      field={field}
                      form={formikProps}
                      label="Size (sqm)"
                    />
                  )}
                />
                <Field
                  name="suburb"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup {...field} mr={4} field={field} form={formikProps} label="Suburb" />
                  )}
                />
                <Field
                  name="rooms"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup {...field} type="number" field={field} min={0} form={formikProps} label="Rooms" />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="salesAgent"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormGroup {...field} mr={4} field={field} form={formikProps} label="Sales Agent" />
                  )}
                />
                <Field
                  name="type"
                  render={({ field }: FieldProps<DetailsFormValues>) => (
                    <StyledFormSelect {...field} field={field} label="Type" form={formikProps} options={TYPE_OPTIONS} />
                  )}
                />
              </FieldRow>
            </Card>
            <Card ml={4} flex={1} bg="white" p={4}>
              {props.isNewProperty ? (
                <NewHeroImage onUploadComplete={(data) => setImage(data)} />
              ) : (
                <HeroImage
                  type="property"
                  id={props.initialValues.id}
                  src={props.image ? props.image.scaled : undefined}
                />
              )}
              <Field
                name="status"
                render={({ field }: FieldProps<DetailsFormValues>) => (
                  <StyledFormSelect
                    {...field}
                    field={field}
                    label="Status"
                    form={formikProps}
                    options={STATUS_OPTIONS}
                  />
                )}
              />
              <Field
                name="id"
                render={({ field }: FieldProps<DetailsFormValues>) => (
                  <StyledFormGroup disabled={true} {...field} field={field} form={formikProps} label="Unique ID" />
                )}
              />
              {props.error && (
                <Span width="100%" textAlign="center" color="error" mb={3}>
                  {props.error.message.replace('GraphQL error:', '').trim()}
                </Span>
              )}
              <Button type="submit" text={props.buttonText || 'SUBMIT'} isLoading={props.loading} />
            </Card>
          </StyledForm>
        );
      }}
    />
  );
};

export default CreatePropertyForm;



// WEBPACK FOOTER //
// ./src/forms/properties/PropertyDetailsForm.tsx